import AddIcon from '@mui/icons-material/Add';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Button/Button.tsx';
import { HamburgerIcon } from '../../Navigation/icons.tsx';

export type Session = {
  session_id: string;
  session_display_name?: string | null;
  last_updated?: string | null;
};

export const SESSION_LIST_COLLAPSED_WIDTH = 44;
export const SESSION_LIST_EXPANDED_WIDTH = 200;

type SessionListProps = {
  footerContent?: ReactNode;
  loading: boolean;
  sessionList: Session[];
  onSessionClick: (session: Session) => void;
  onNewSessionClick: () => void;
  onCollapsedChange?: (collapsed: boolean) => void;
  selectedSessionId: string | null;
};

type ChatButtonProps = {
  chat: Session;
  isSelected: boolean;
  onChatClick: (chat: Session) => void;
};

export const SessionList = ({
  footerContent,
  loading,
  sessionList,
  onSessionClick,
  onNewSessionClick,
  onCollapsedChange,
  selectedSessionId,
}: SessionListProps) => {
  const [collapsed, setCollapsed] = useState(false);

  const { t } = useTranslation();

  const handleCollapse = () => {
    const nextCollapsed = !collapsed;
    setCollapsed(nextCollapsed);
    onCollapsedChange?.(nextCollapsed);
  };

  return (
    <Stack
      direction={'column'}
      data-test="session-history-container"
      sx={{
        gap: 2,
        width: collapsed ? `${SESSION_LIST_COLLAPSED_WIDTH}px` : `${SESSION_LIST_EXPANDED_WIDTH}px`,
        flexShrink: 0,
        backgroundColor: 'background.boxDark',
        padding: 1,
        overflow: 'hidden',
        transition: 'width 0.2s ease-in-out',
      }}
    >
      <IconButton
        aria-label={collapsed ? t('Expand session history') : t('Collapse session history')}
        sx={{ width: '28px', height: '28px' }}
        onClick={handleCollapse}
      >
        <HamburgerIcon />
      </IconButton>
      {collapsed ? (
        <IconButton sx={{ width: '28px', height: '28px', alignSelf: 'center' }} onClick={onNewSessionClick}>
          <AddIcon sx={{ color: 'primary.main' }} />
        </IconButton>
      ) : (
        <Button
          variant="text"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          sx={{ flexWrap: 'nowrap', textWrap: 'nowrap' }}
          onClick={onNewSessionClick}
        >
          {collapsed ? undefined : t('New chat')}
        </Button>
      )}
      {collapsed ? null : (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          <Typography
            variant="body1Bold"
            sx={{
              color: 'text.secondary',
              paddingX: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {t('History')}{' '}
            <Tooltip
              title={t('Sessions automatically expire 30 days after creation')}
              placement="right"
              slotProps={{
                tooltip: {
                  sx: { maxWidth: '400px' },
                },
              }}
            >
              <HelpIcon sx={{ fontSize: '16px' }} />
            </Tooltip>
          </Typography>
          <Stack
            direction="column"
            sx={{
              gap: 0,
              flexGrow: 1,
              overflow: 'auto',
            }}
          >
            {loading ? (
              <>
                <Skeleton animation="wave" width="75%" />
                <Skeleton animation="wave" width="75%" />
                <Skeleton animation="wave" width="75%" />
              </>
            ) : sessionList.length === 0 ? (
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', paddingX: '4px', marginTop: 3, textAlign: 'center' }}
                data-test="no-sessions-message"
              >
                {t('Start a new chat to see your history.')}
              </Typography>
            ) : (
              sessionList.map(session => (
                <ChatButton
                  key={session.session_id}
                  chat={session}
                  isSelected={selectedSessionId === session.session_id}
                  onChatClick={onSessionClick}
                />
              ))
            )}
          </Stack>
          {footerContent}
        </Box>
      )}
    </Stack>
  );
};

const ChatButton = ({ chat, isSelected, onChatClick }: ChatButtonProps) => {
  return (
    <Button
      key={chat.session_id}
      variant="text"
      size="medium"
      color="primary"
      data-test={`session-history-item`}
      sx={{
        justifyContent: 'flex-start',
        color: 'text.primary',
        textTransform: 'none',
        fontWeight: isSelected ? '700' : '600',
      }}
      onClick={() => {
        onChatClick(chat);
      }}
    >
      <Tooltip title={chat.session_display_name} placement="right">
        <Box
          sx={{
            justifyContent: 'flex-start',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {chat.session_display_name}
        </Box>
      </Tooltip>
    </Button>
  );
};
